/**
 * OpenEXR decoder built on three.js' EXRLoader (supports NONE, RLE, ZIPS,
 * ZIP, PIZ, PXR24, B44, B44A and DWA compression; half and float channels).
 * The module is loaded lazily so three.js only ships when an EXR is opened.
 */
import type { EncodingHint, RawFrame } from '../../core/types';
import { flipRows, makeFrame } from '../frame';
import { matchPrimaries } from '../icc';

export async function decodeExr(bytes: Uint8Array, name: string): Promise<RawFrame> {
  const [{ EXRLoader }, { FloatType }] = await Promise.all([import('three/examples/jsm/loaders/EXRLoader.js'), import('three')]);
  const loader = new EXRLoader();
  loader.setDataType(FloatType);
  const buf = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
  const r = loader.parse(buf) as unknown as {
    width: number;
    height: number;
    data: Float32Array;
    header: Record<string, unknown>;
  };
  const { width, height } = r;
  const channels = r.data.length / (width * height);
  const data = r.data;
  // EXRLoader writes rows bottom-up for WebGL; restore top-down order.
  flipRows(data, width, height, channels);
  const header = r.header ?? {};
  const chNames = ((header.channels as { name: string }[]) ?? []).map((c) => c.name);
  const isGray = chNames.length > 0 && chNames.every((n) => n === 'Y' || n === 'A');
  let encoding: EncodingHint = {
    transfer: 'linear',
    primaries: 'srgb',
    source: 'format',
    description: 'OpenEXR scene-linear data; no chromaticities attribute, BT.709/sRGB primaries assumed',
  };
  const chroma = header.chromaticities as
    | { redX: number; redY: number; greenX: number; greenY: number; blueX: number; blueY: number; whiteX: number; whiteY: number }
    | undefined;
  if (chroma) {
    const c = {
      r: [chroma.redX, chroma.redY] as [number, number],
      g: [chroma.greenX, chroma.greenY] as [number, number],
      b: [chroma.blueX, chroma.blueY] as [number, number],
      w: [chroma.whiteX, chroma.whiteY] as [number, number],
    };
    const p = matchPrimaries(c);
    encoding = {
      transfer: 'linear',
      primaries: p,
      chromaticities: p === 'custom' ? c : undefined,
      source: 'format',
      description: 'OpenEXR chromaticities attribute',
    };
  }
  const info: Record<string, string> = {
    Channels: chNames.join(', ') || `${channels}`,
    Compression: String(header.compression ?? ''),
  };
  if (typeof header.whiteLuminance === 'number') info['White luminance'] = `${header.whiteLuminance} cd/m² per unit`;
  let out = data;
  let outCh = channels;
  if (isGray && channels === 4) {
    // Luminance-only EXR was expanded to RGBA with R=G=B.
    out = new Float32Array(width * height);
    for (let i = 0; i < out.length; i++) out[i] = data[i * 4];
    outCh = 1;
  }
  return makeFrame({
    width,
    height,
    data: out,
    channels: outCh,
    isFloat: true,
    bitDepth: 32,
    format: 'exr',
    name,
    encoding,
    info,
  });
}
